/**
 * Parkers — the manager's side of staff self-registration.
 *
 * Pending requests at the top (the only actionable thing on the screen), the
 * decided ones below as a record. Approving is the one action in this portal
 * that grants free parking, so the confirm step states what is actually being
 * granted — which plate, which dates — rather than asking "Approve?".
 *
 * ⚠️ Two things the UI says out loud because the underlying model can't express
 * them, and a manager who doesn't know will be surprised:
 *   - A permit approved HERE has no hours. The model supports a weekly schedule
 *     (`Permit.accessHours`) and enforcement honours it, but this flow does not
 *     set one — so "Approved through Dec 31" still means 24/7 until then, not
 *     "during shifts". Do not soften this copy because the field exists; soften
 *     it when this form actually writes one.
 *     What DID change (PR 4, spec §17): the copy no longer says a permit *can't*
 *     be limited to hours, because it can — it says THIS FORM can't set them and
 *     names who can. The distinction matters: a manager who reads the old
 *     sentence and later sees a scheduled permit on Permits.tsx has been told
 *     two contradictory things by the same product.
 *   - The end date is mandatory. The server rejects an open-ended grant, so the
 *     picker has no "no expiry" option to choose by accident.
 *
 * The seat cap shown here is advisory: the SERVER re-checks it at the moment of
 * approval. A count rendered in a browser minutes ago is not a cap.
 */
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import type { TenantSession } from '../lib/session'
import {
  listRegistrations, approveRegistration, declineRegistration, listTenantPermits, updateTenantPermitVehicle, previewPaidNotice, PAID_REMOVAL_REFUSED_CODE, type PaidNoticePreview, revokeProgramSeat, addParker, tenantCan,
  type TenantRegistration, type RegistrationEmailKind, type TenantPermitBilling, type RosterPermit, type RosterVehicle, type MeResponse,
  type PlateStateInput, type TenantVehicleEdit,
  listProgramAllocations, type ProgramAllocation,
} from '../lib/api'
import { TYPE_LABEL, typeLabel, DEFAULT_PERMIT_TYPE, type TypePressure } from '../lib/permitTypes'
import { TypeChip } from '../components/TypeChip'
import { AddParkerActions, type NamedPermitOption } from '../components/AddParkerActions'
import { labelStyle, cardTitle, purpleBtn, ghostBtn, buttonRadius, selectStyle, modalSection, modalActions } from '../ui'
import { permitEndLabel, permitStartLabel, todayCalendarDate } from '../lib/accountState'
import { PAID_NOTICE_DAYS, earliestPaidEndDate, endDateAllowed, friendlyDate, removableNow, tenantPaysMonthly } from '../lib/paidNotice'
import { isChunkLoadError } from '../lib/chunkRecovery'
import { summarizeTenantWelcomeRoster } from '../lib/welcome'
import { lapseInfo, queuePlates } from '../lib/registrationQueue'
import { DatePicker, PLATE_STATE_GROUPS, PLATE_STATE_UNKNOWN, PLATE_STATE_UNKNOWN_LABEL, RowActions, RowCue, RowOpen, isPlateStateChoice, isPlateStateCode, plateStateChoice } from '@parkify/ui'
import { ParkerPhoneField } from '../components/ParkerPhoneField'
import { TenantModal } from '../components/TenantModal'
import { formatStoredParkerPhone, normalizeParkerPhone } from '../../../../packages/account/src/parkerPhone'

/**
 * The sentence for the approve modal's billing line.
 *
 * ⚠️ NO ARITHMETIC HERE ON PURPOSE. Whether the next space is billable is an
 * off-by-one away from the current `included`/`billable` split (a tenant with
 * 12 included and 12 held has `billable: 0`, and their next approval is the
 * first one they pay for), and getting it wrong is a surprise charge. The
 * server decides it — `deriveNextSpaceBilling` in api/src/tenants/permit_billing.ts,
 * which is the side of the wire CI tests. This function only picks words.
 *
 * Returns null under an `included` lease: every space is free however many they
 * hold, so there is no question to answer, and an inapplicable row is hidden
 * rather than greyed out.
 *
 * COUNTS ONLY — no amount appears, because the server sends none.
 */
function billingSentence(b: TenantPermitBilling | null): string | null {
  if (!b) return null
  const nth = ordinal(b.next.nth)
  switch (b.model) {
    case 'included':
      return null
    case 'purchased':
      // The bold lead already says "billable" — this adds the count, not a
      // second copy of the verdict.
      return `You buy each staff space, and this would be your ${nth}.`
    case 'overage':
      return b.next.billable
        ? `This would be your ${nth} staff space, and your lease covers ${b.next.includedPermitCount}.`
        : `This would be your ${nth} staff space, of the ${b.next.includedPermitCount} your lease covers.`
  }
}

/** 1 → "1st", 13 → "13th". The teens are the whole reason this isn't a lookup. */
function ordinal(n: number): string {
  const rem100 = n % 100
  if (rem100 >= 11 && rem100 <= 13) return `${n}th`
  switch (n % 10) {
    case 1: return `${n}st`
    case 2: return `${n}nd`
    case 3: return `${n}rd`
    default: return `${n}th`
  }
}

const overlay: CSSProperties = { position: 'fixed', inset: 0, background: 'rgba(35,31,32,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 16 }
const modal: CSSProperties = { width: 'min(520px, 94vw)', maxHeight: '92vh', overflowY: 'auto', background: '#fff', borderRadius: 16, padding: 24, boxShadow: '0 20px 60px rgba(0,0,0,0.35)' }

const fmtDate = (iso?: string) => {
  if (!iso) return '—'
  try {
    const [year, month, day] = iso.slice(0, 10).split('-').map(Number)
    if (!year || !month || !day) return iso
    return new Date(year, month - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  }
  catch { return iso }
}

const waitingLabel = (createdAt: string) => {
  const elapsed = Math.max(0, Date.now() - new Date(createdAt).getTime())
  const days = Math.max(1, Math.floor(elapsed / 86_400_000))
  return `Waiting ${days} day${days === 1 ? '' : 's'}`
}

/**
 * Same tones Permits.tsx uses for a permit's status — kept in sync
 * deliberately, since a roster row and a permit card describe the exact same
 * server-computed status and a manager reading both screens should never see
 * two different colors mean the same thing.
 */
const STATUS_TONE: Record<RosterPermit['status'], { bg: string; fg: string; border: string; label: string }> = {
  active: { bg: 'rgba(21,128,61,0.10)', fg: '#15803d', border: 'rgba(21,128,61,0.30)', label: 'Active' },
  expiringSoon: { bg: 'rgba(245,158,11,0.12)', fg: '#92400e', border: 'rgba(245,158,11,0.35)', label: 'Ending soon' },
  expired: { bg: 'var(--color-gray-100)', fg: 'var(--color-gray-600)', border: 'var(--color-gray-200)', label: 'Not active' },
}

/**
 * Width of the roster's Status column, in px.
 *
 * Must be >= the widest rendered pill. The status text is uppercase and tracked,
 * so its rendered width is wider than the raw label; 144px leaves air for
 * "ENDING SOON" and prevents the pill from bleeding into Actions.
 */
const STATUS_COL = 144

const ACTION_COL = 166
const tableActionBtn: CSSProperties = {
  ...ghostBtn, padding: '5px 10px', color: 'var(--color-gray-900)',
  fontSize: 12, fontWeight: 800,
}
const tableRemoveBtn: CSSProperties = {
  ...ghostBtn, padding: '5px 10px', color: '#7F3F98',
  fontSize: 12, fontWeight: 800,
}
/** The confirm button of Decline and of a free-roster Remove: the demo's `danger` kind. */
const dangerBtn: CSSProperties = { ...ghostBtn, color: '#b91c1c', borderColor: 'rgba(185,28,28,0.4)' }

/** Let the roster consume its full card width. The old 320px ceiling left a
 * large inert strip after Actions on wide tenant dashboards. */
const NAME_COL = 'minmax(180px, 1.6fr)'

/** One flattened roster row per vehicle on a permit — the unit a person recognizes. */
interface RosterRow {
  key: string
  permitId: string
  name: string
  plate: string
  permitType?: string
  /** Every plate on the underlying permit. This is intentionally separate
   * from `plates`, which contains only the selected parker's vehicles. */
  permitPlates: string[]
  plates: string[]
  parkerVehicles: RosterVehicle[]
  startDate?: string
  validUntil: string
  permitLabel: string
  status: RosterPermit['status']
  phone?: string
  email?: string
  description?: string
  /** Shared-program seat behind this row; Remove releases the whole seat (all of this parker's vehicles). */
  programIssuanceId?: string
  /** The shared program behind `programIssuanceId`, for its release notice. */
  programId?: string
  /** Reserved bays the permit holds (ids); rendered through the program pool's labels. */
  bayIds?: string[]
  state?: string
  /** Every zone the underlying permit is honored in — a permit can cover more
   *  than one, so this is a list, not a single zone. */
  zoneIds: string[]
  /** Plates per parker on this row's permit (read-only, set on the location by the operator). */
  platesPerParker?: number
}

/** A handful of accent colors, cycled by a stable hash so the same name/plate
 * always lands on the same color across reloads — not random, not sequential
 * (sequential would re-shuffle every time the list re-sorts or re-filters). */
const AVATAR_PALETTE = ['#0EA5E9', '#F59E0B', '#7F3F98', '#15803D', '#D97706', '#213F99', '#ED1C24']
function hashColor(s: string): string {
  let h = 0
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0
  return AVATAR_PALETTE[Math.abs(h) % AVATAR_PALETTE.length]
}

/** First letter of each of up to two words — "Dana Kim" → "DK", "RQW5537" → "R". */
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'
  return (parts[0][0] + (parts[1]?.[0] ?? '')).toUpperCase()
}

/**
 * `tone="pending"` swaps the name-hashed fill for the pending orange.
 *
 * The hash colour exists to make one person's row findable in a long roster; on
 * the approval queue every row is the same KIND of thing, so the colour is
 * better spent saying "this is waiting on you" than distinguishing Dana from
 * Marcus. Text stays orange-on-pale rather than white-on-orange so the initials
 * keep their contrast at 12px.
 */
function Avatar({ name, tone }: { name: string; tone?: 'pending' }) {
  const pending = tone === 'pending'
  return (
    <div style={{
      width: 32, height: 32, borderRadius: '50%', flex: '0 0 auto',
      background: pending ? '#FEF1EA' : hashColor(name),
      color: pending ? '#F16521' : '#fff',
      fontSize: 12, fontWeight: 800,
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      {initials(name)}
    </div>
  )
}

/** Plates belonging to the same parker as `vehicle`.
 *
 * A permit can cover several people, and it can also cover several plates for
 * one person. The editor is for one parker, so do not hand it every plate on
 * the permit. Prefer the driver's name when present; older records may only
 * have contact detail, and plate-only records must remain isolated to the
 * selected vehicle.
 */
function vehiclesForParker(vehicles: RosterVehicle[], vehicle: RosterVehicle): RosterVehicle[] {
  // A roster pool names each person with a holder id. Prefer it: two staff who
  // share a name are two people, and the server counts them as two seats.
  if (vehicle.holderId) return vehicles.filter(candidate => candidate.holderId === vehicle.holderId)
  const driver = vehicle.driver?.trim().toLowerCase()
  if (driver) {
    const sameDriver = vehicles
      .filter(candidate => candidate.driver?.trim().toLowerCase() === driver)
    if (sameDriver.length) return sameDriver
  }

  const email = vehicle.email?.trim().toLowerCase()
  if (email) {
    const sameEmail = vehicles
      .filter(candidate => candidate.email?.trim().toLowerCase() === email)
    if (sameEmail.length) return sameEmail
  }

  const phone = vehicle.phone?.replace(/\D/g, '')
  if (phone) {
    const samePhone = vehicles
      .filter(candidate => candidate.phone?.replace(/\D/g, '') === phone)
    if (samePhone.length) return samePhone
  }

  return [vehicle]
}

/** Flatten every permit's vehicles into one roster row PER PARKER — not per
 * plate. `vehiclesForParker` already groups a permit's vehicles by driver
 * (falling back to email, then phone) to build each row's `parkerVehicles`;
 * this loop must skip every plate that grouping already claimed, or a parker
 * with two cars renders as two identical rows and every headline count that
 * sums roster ROWS (rather than `summarizeTenantWelcomeRoster`'s own
 * identity grouping) double-counts them — the #dev-requests report (Maecy,
 * 2026-09-25): Just Love Coffee (GA1110-A) showed Lun Vung and Yulisa Sanchez
 * twice each in "All parkers", inflating "Total parkers".
 *
 * A vehicle with no `driver` filled in (pre-2026-08-11 permits, or an ops
 * entry that skipped it) falls back to its plate as the display name AND as
 * its own identity — never a blank row, and never grouped with another
 * plate-only vehicle it shares no proven identity with. */
/** Earliest enforcement start among a parker's plates; validFrom where the api sent none. */
export function parkerStartDate(p: Pick<RosterPermit, 'validFrom' | 'plateStartDates'>, plates: string[]): string | undefined {
  const days = plates.map(plate => p.plateStartDates?.[plate] || p.validFrom).filter(Boolean).sort()
  return days[0] || p.validFrom || undefined
}

export function flattenRoster(permits: RosterPermit[]): RosterRow[] {
  const out: RosterRow[] = []
  for (const p of permits) {
    const claimed = new Set<string>()
    for (const v of p.vehicles) {
      if (claimed.has(v.plate)) continue
      const parkerVehicles = vehiclesForParker(p.vehicles, v)
      for (const vehicle of parkerVehicles) claimed.add(vehicle.plate)
      out.push({
        key: `${p.id}:${v.plate}`,
        permitId: p.id,
        name: v.driver?.trim() || v.plate,
        plate: v.plate,
        permitType: p.permitType,
        permitPlates: p.vehicles.map(vehicle => vehicle.plate),
        plates: parkerVehicles.map(vehicle => vehicle.plate),
        ...(p.platesPerParker ? { platesPerParker: p.platesPerParker } : {}),
        parkerVehicles,
        // The parker's own start, not the permit's: a plate added after the
        // permit began is covered from the day it was added (#4080/#4087).
        // With several plates, the earliest is when this parker was first covered.
        startDate: parkerStartDate(p, parkerVehicles.map(vehicle => vehicle.plate)),
        validUntil: p.validUntil,
        permitLabel: p.label,
        status: p.status,
        phone: v.phone,
        email: v.email,
        description: v.description,
        ...(p.issuanceId ? { programIssuanceId: p.issuanceId, ...(p.programId ? { programId: p.programId } : {}) } : {}),
        ...(p.bayIds?.length ? { bayIds: p.bayIds } : {}),
        state: v.state,
        zoneIds: p.zoneIds ?? [],
      })
    }
  }
  return out
}

/** Search text matches the parker's name, the email shown on the row, or any
 *  plate on the row. Every plate, not just the primary one: a two-plate parker
 *  must be findable by either plate, since the row stands for both. `q` is
 *  already trimmed and lowercased; an empty query matches everything. */
export function rosterRowMatchesSearch(
  r: Pick<RosterRow, 'name' | 'email' | 'plates'>,
  q: string,
): boolean {
  if (!q) return true
  return r.name.toLowerCase().includes(q)
    || (r.email?.toLowerCase().includes(q) ?? false)
    || r.plates.some(plate => plate.toLowerCase().includes(q))
}

/** Permit buckets the operator has already made available to this tenant. */
export function availablePermitTypes(permits: RosterPermit[]): string[] {
  const live = permits.filter(permit => permit.active && permit.status !== 'expired')
  return Object.keys(TYPE_LABEL).filter(type => (
    live.some(permit => (permit.permitType || DEFAULT_PERMIT_TYPE) === type)
  ))
}

/**
 * Removing a parker from a shared permit must retain every other parker on
 * that permit. `parkerPlates` can contain more than one plate when one person
 * has multiple cars; it must never be confused with the permit-wide roster.
 */
export function remainingPermitPlates(
  permitPlates: string[],
  parkerPlates: string[],
): string[] {
  const removing = new Set(parkerPlates)
  return permitPlates.filter(plate => !removing.has(plate))
}

/** Escapes a value for one CSV cell — same rule ops's Accounting.tsx export
 * uses: quote it, and double up any embedded quotes. */
function csvCell(v: string): string {
  return `"${v.replace(/"/g, '""')}"`
}

/** "CUZ7498" · with a known state, "CUZ7498 · GA" — never invents a state
 * that isn't on the vehicle record. Mirrors the demo's `cPlateLabel`
 * (parkify-demo-v2-wip.jsx:2667) so the two surfaces read the same way. */
function plateLabel(plate: string, state?: string, stateUnknown?: boolean): string {
  if (state) return `${plate} · ${state}`
  return stateUnknown ? `${plate} · needs state` : plate
}

/**
 * A select value as the wire carries it: the "State unknown" choice (ruling
 * 2026-10-21) goes as `stateUnknown: true` with an empty state, never as the
 * string "UNKNOWN". A blank goes as a blank, which the server still refuses.
 */
function plateStateInput<T extends { state: string }>(row: T): T & { stateUnknown?: true } {
  return row.state === PLATE_STATE_UNKNOWN ? { ...row, state: '', stateUnknown: true } : row
}

export function Parkers({ session, me, hideMonetaryValues = false, initialTypeFilter, initialPermitFilter, autoOpenAdd, onAutoOpenAddHandled }: {
  session: TenantSession
  me: MeResponse
  hideMonetaryValues?: boolean
  /**
   * Pre-selects a permit-type chip — set when a manager arrives here by
   * clicking "View parkers" on a Permits capacity card, so they land on the
   * bucket they asked about rather than the whole roster. App.tsx clears it on
   * any normal nav click, so it never silently re-applies later.
   */
  initialTypeFilter?: string | null
  /**
   * Pre-selects one named permit — set when the manager clicks "View parkers"
   * on a named permit's card (Canvas Properties vs Otto's). The type alone
   * can't do it: both lists are Employee permits, so a type drill landed on
   * every Employee row and read as no filter at all (Maecy, 2026-10-23).
   */
  initialPermitFilter?: string | null
  autoOpenAdd?: boolean
  onAutoOpenAddHandled?: () => void
}) {
  const [rows, setRows] = useState<TenantRegistration[]>([])
  // `used: null` = the count is unavailable. Rendered as "—", never as 0: a
  // manager who reads "0 of 25 used" on a failed count believes they have room.
  // Absent = the operator hasn't set a permit model, or the count failed. Both
  // mean "say nothing about money" — see the api.ts note; there is no default
  // that isn't a lie in one direction or the other.
  const [billing, setBilling] = useState<TenantPermitBilling | null>(null)
  // Absent = the operator configured no per-type sub-caps, so the single
  // overall number above is the whole story. Not an error, and not zero.
  const [seatTypes, setSeatTypes] = useState<{
    caps: Record<string, number>
    usedByType: Record<string, number>
    pressure?: Record<string, TypePressure>
  } | null>(null)
  const [loading, setLoading] = useState(true)
  const [err, setErr] = useState('')
  const [approving, setApproving] = useState<TenantRegistration | null>(null)
  // The request or roster row whose Decline or Remove dialog is open.
  const [declining, setDeclining] = useState<TenantRegistration | null>(null)
  const [removing, setRemoving] = useState<RosterRow | null>(null)
  // This tenant's shared-program allocations, for bay labels on the roster and
  // the pool picker at approval. Best effort: null hides both, never invents.
  const [programs, setPrograms] = useState<ProgramAllocation[] | null>(null)
  const bayLabel = useMemo(() => {
    const m = new Map<string, string>()
    for (const p of programs ?? []) for (const b of p.bayPool ?? []) m.set(b.bayId, b.label)
    return m
  }, [programs])
  const [adding, setAdding] = useState(false)
  const [busy, setBusy] = useState(false)
  const [permits, setPermits] = useState<RosterPermit[]>([])
  // Plates per parker for a NEW parker (the tenant's lots); each row carries its own permit's.
  const [platesPerParker, setPlatesPerParker] = useState<number | undefined>(undefined)
  // Named permits (operator setting): every add, invite and approval names one
  // of these. null = the ordinary by-type tenant.
  const [namedPermits, setNamedPermits] = useState<NamedPermitOption[] | null>(null)
  // Kept separate from `err` on purpose: "Waiting for approval" is the only
  // actionable thing on this screen (see the file header), and a failed
  // permit-roster read must not blank out a working approval queue just
  // because the two reads happen to share one load() call.
  const [rosterErr, setRosterErr] = useState('')
  const [search, setSearch] = useState('')
  const [exportOpen, setExportOpen] = useState(false)
  const [pdfExporting, setPdfExporting] = useState(false)
  const [pdfExportError, setPdfExportError] = useState<'stale-chunk' | 'failed' | null>(null)
  // Roster filters. `null` = "All" — kept as null rather than a sentinel string
  // so a permit type literally named 'all' could never collide with it.
  const [typeFilter, setTypeFilter] = useState<string | null>(initialTypeFilter ?? null)
  const [zoneFilter, setZoneFilter] = useState<string | null>(null)
  // One named permit inside the type (a permit id). Only offered to a
  // named-permits tenant; see `permitOptions`.
  const [permitFilter, setPermitFilter] = useState<string | null>(initialPermitFilter ?? null)
  // A drill from Permits re-applies even if this screen is still mounted, so a
  // second pick is never ignored because useState only reads its first value.
  useEffect(() => {
    setTypeFilter(initialTypeFilter ?? null)
    setPermitFilter(initialPermitFilter ?? null)
  }, [initialTypeFilter, initialPermitFilter])
  const [editing, setEditing] = useState<RosterRow | null>(null)
  // Paying tenants give 30 days' notice to end a parker (lib/paidNotice.ts).
  const paidMonthly = tenantPaysMonthly(me.tenant)
  // The end date Remove pre-fills for a paying tenant; cleared when the editor closes.
  const [suggestedEnd, setSuggestedEnd] = useState<string | undefined>(undefined)

  useEffect(() => {
    if (autoOpenAdd) {
      setAdding(true)
      onAutoOpenAddHandled?.()
    }
  }, [autoOpenAdd, onAutoOpenAddHandled])

  const load = async () => {
    setLoading(true); setErr(''); setRosterErr('')
    try {
      const res = await listRegistrations(session.token)
      listProgramAllocations(session.token).then(r => setPrograms(r.programs)).catch(() => setPrograms(null))
      setRows(res.registrations); setBilling(res.billing ?? null)
      setSeatTypes(res.seatTypes ?? null)
    } catch (e) {
      setErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] registrations', e)
    }
    try {
      const res = await listTenantPermits(session.token)
      setPermits(res.permits)
      setPlatesPerParker(res.platesPerParker)
      setNamedPermits(res.permitsByName
        ? res.permits.filter(p => p.status !== 'expired' && !p.programId).map(p => ({ id: p.id, label: p.label }))
        : null)
    } catch (e) {
      setRosterErr(e instanceof Error ? e.message : 'load failed'); console.error('[tenant] permits', e)
    } finally { setLoading(false) }
  }

  useEffect(() => {
    if (session.kind === 'device') return   // a tablet 403s here; App.tsx never routes one
    void load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Hooks must run unconditionally on every render, so these sit ABOVE the
  // device early-return below even though a tablet session never uses them.
  const roster = useMemo(() => flattenRoster(permits), [permits])
  const addParkerPermitTypes = useMemo(() => availablePermitTypes(permits), [permits])
  // The table is one row per vehicle; the headline is one count per parker.
  // Reuse the welcome's identity grouping so the two live surfaces agree.
  const rosterSummary = useMemo(() => summarizeTenantWelcomeRoster(permits), [permits])
  const activeCount = rosterSummary.parkerCount
  // Zone id → display name. The roster carries ids (they're what the permit
  // record stores); only `me.zones` knows what to call them.
  const zoneName = useMemo(() => {
    const byId = new Map(me.zones.map(z => [z.id, z.displayName]))
    return (id: string) => byId.get(id) ?? id
  }, [me.zones])

  // The zone column and picker only earn their space on a tenant that actually
  // spans lots. On a single-zone tenant every row would repeat one name.
  const multiZone = me.zones.length > 1
  // The picker only earns its place when some zone would change the list. A
  // permit grants every zone it lists to every car on it, so a tenant whose
  // permits all cover all of its lots (Canvas Properties: 2 permits, 3 zones)
  // got the same rows back for every pick, which read as a filter that broke.
  // `|| zoneFilter` keeps an escape hatch if a reload removes the split.
  const zoneCanNarrow = multiZone && (zoneFilter !== null
    || me.zones.some(z => roster.some(r => !r.zoneIds.includes(z.id))))

  // Everything except the type filter, so the chip counts below can say what
  // picking each chip would actually yield.
  const searchZoneFiltered = useMemo(() => {
    const q = search.trim().toLowerCase()
    return roster.filter(r =>
      rosterRowMatchesSearch(r, q)
      && (!zoneFilter || r.zoneIds.includes(zoneFilter)),
    )
  }, [roster, search, zoneFilter])

  /**
   * Per-type row counts for the filter chips.
   *
   * ⚠️ Counts ROSTER ROWS — one per vehicle — and deliberately NOT
   * `seatTypes.usedByType`, which counts SEATS (permits). The two are different
   * numbers for a permit covering more than one car, and a "Staff 9" chip
   * sitting above a list of 12 staff rows is a contradiction a manager has to
   * resolve themselves. Seat counts have their own home: the capacity rows
   * higher up this screen, where the cap they're measured against also lives.
   */
  const typeCounts = useMemo(() => {
    const out = new Map<string, number>()
    for (const r of searchZoneFiltered) {
      const t = r.permitType || DEFAULT_PERMIT_TYPE
      out.set(t, (out.get(t) ?? 0) + 1)
    }
    return out
  }, [searchZoneFiltered])

  const typeFiltered = useMemo(() => (
    typeFilter
      ? searchZoneFiltered.filter(r => (r.permitType || DEFAULT_PERMIT_TYPE) === typeFilter)
      : searchZoneFiltered
  ), [searchZoneFiltered, typeFilter])

  // Named permits of the type in view, with how many rows picking each would
  // show. The type in view is the picked chip, or the only type the roster has
  // (Canvas Properties: two Employee lists, no chip row at all). Offered only
  // when there are two to choose between; `permitFilter` keeps a stale pick
  // listed so it can always be cleared.
  const permitScopeType = useMemo(() => {
    if (typeFilter) return typeFilter
    const rosterTypes = new Set(roster.map(r => r.permitType || DEFAULT_PERMIT_TYPE))
    return rosterTypes.size === 1 ? [...rosterTypes][0]! : null
  }, [roster, typeFilter])
  const permitOptions = useMemo(() => {
    if (!namedPermits) return []
    if (!permitScopeType) return []
    const typeOf = (id: string) => permits.find(p => p.id === id)?.permitType || DEFAULT_PERMIT_TYPE
    const ids = namedPermits.map(p => p.id).filter(id => typeOf(id) === permitScopeType)
    if (permitFilter && !ids.includes(permitFilter)) ids.push(permitFilter)
    return ids.map(id => ({
      id,
      label: namedPermits.find(p => p.id === id)?.label ?? permits.find(p => p.id === id)?.label ?? id,
      count: typeFiltered.filter(r => r.permitId === id).length,
    }))
  }, [namedPermits, permits, permitScopeType, permitFilter, typeFiltered])
  const permitPickerShown = !loading && !rosterErr && (permitOptions.length >= 2 || permitFilter !== null)
  const permitFilterLabel = permitFilter ? permitOptions.find(o => o.id === permitFilter)?.label : undefined

  const filteredRoster = useMemo(() => (
    permitFilter ? typeFiltered.filter(r => r.permitId === permitFilter) : typeFiltered
  ), [typeFiltered, permitFilter])

  const handleApprove = async (reg: TenantRegistration, validUntil: string, plateStates: PlateStateInput[], bayId?: string | null, permitId?: string | null) => {
    if (busy) return
    setBusy(true)
    try {
      await approveRegistration(session.token, reg.id, validUntil, plateStates, bayId, permitId)
      setApproving(null)
      await load()
    } catch (e) {
      // The server owns the vocabulary here — a seat-cap refusal names the
      // number, so show its message rather than a generic failure.
      alert(e instanceof Error ? e.message : 'Could not approve that request.')
    } finally { setBusy(false) }
  }

  // Runs from the Decline dialog's confirm button.
  const handleDecline = async (reg: TenantRegistration) => {
    setDeclining(null)
    try { await declineRegistration(session.token, reg.id); await load() }
    catch (e) { alert(e instanceof Error ? e.message : 'Could not decline that request.') }
  }

  const handleAddParker = async (parker: {
    firstName: string; lastName: string; plate?: string; plates: string[]; plateStates: PlateStateInput[]; permitType: string; validUntil: string
    phone: string; email?: string; permitId?: string
  }) => {
    if (busy) return
    setBusy(true)
    try {
      await addParker(session.token, parker)
      setAdding(false)
      await load()
    } catch (e) {
      // Same seat-cap check as approving a registration — the server owns the
      // wording, so show its message rather than a generic failure.
      alert(e instanceof Error ? e.message : 'Could not add that parker.')
    } finally { setBusy(false) }
  }

  const handleEdit = async (row: RosterRow, detail: EditParkerDetail) => {
    if (busy) return
    setBusy(true)
    try {
      await updateTenantPermitVehicle(session.token, row.permitId, row.plate, {
        driver: detail.name,
        description: detail.description,
        phone: detail.phone,
        email: detail.email,
        originalPlates: row.parkerVehicles.map(vehicle => vehicle.plate),
        vehicleEdits: detail.vehicleEdits,
        permitType: detail.permitType,
        validUntil: detail.validUntil,
      })
      setEditing(null)
      await load()
    } catch (e) {
      alert(paidRemovalExplanation(row.name, e) ?? (e instanceof Error ? e.message : 'Could not update that parker.'))
    } finally { setBusy(false) }
  }

  const handleRemove = async (row: RosterRow) => {
    if (busy) return
    if (row.programIssuanceId) {
      // A program seat is the whole holder: every vehicle on it goes at once,
      // and the seat returns to your allocation only after the server confirms.
      // A program with a release notice (set by Parkify) ends the seat on a
      // date instead; the server picks it on the lot's clock and says which.
      const noticeDays = (programs ?? []).find(p => p.id === row.programId)?.releaseNoticeDays ?? 0
      const vehicles = row.plates.length === 1 ? 'of their vehicles' : `${row.plates.length} of their vehicles`
      if (!confirm(noticeDays > 0
        ? `Give notice for ${row.name}? This program requires ${noticeDays} day${noticeDays === 1 ? '' : 's'}’ notice: all ${vehicles} keep access, and the seat stays in use, until the notice ends.`
        : `Remove ${row.name} from your shared program? All ${vehicles} lose access and the seat returns to your allocation.`)) return
      setBusy(true)
      try {
        const res = await revokeProgramSeat(session.token, row.programIssuanceId)
        // Trust the answer, not the program list read earlier: notice may have been turned on or off since.
        if (res.releaseNotice) alert(`${row.name}’s seat ends ${friendlyDate(res.releaseNotice.endsOn)}. They keep access until then, and the seat returns to your allocation after that date.`)
        await load()
      }
      catch (e) { alert(e instanceof Error ? e.message : 'Could not remove that parker.') }
      finally { setBusy(false) }
      return
    }
    // A tenant that pays for its monthly parkers gives notice instead (the API
    // refuses the removal either way): open the editor on the end date.
    // A paying tenant ends a parker with an end date (Austin, 2026-10-16): Remove
    // opens the editor with the date set 30 days out, which they can move earlier.
    if (!removableNow({ paid: paidMonthly, validUntil: row.validUntil })) {
      const suggested = earliestPaidEndDate()
      setSuggestedEnd(!row.validUntil || row.validUntil > suggested ? suggested : undefined)
      setEditing(row)
      return
    }
    // A free roster removes immediately, after the Remove dialog.
    setRemoving(row)
  }

  // Runs from the free roster's Remove dialog.
  const removeFromFreeRoster = async (row: RosterRow) => {
    setRemoving(null)
    if (busy) return
    setBusy(true)
    try {
      // Name the plates to REMOVE and let the server resolve them against the
      // permit as it is now. Sending the remaining list from this page would
      // drop anyone added to a shared roster permit since the page loaded.
      // Always by plate, even when this page thinks the parker is the last one
      // on the permit: someone may have joined since. The server deletes the
      // permit itself when nothing remains.
      await updateTenantPermitVehicle(session.token, row.permitId, row.plate, {
        removePlates: row.plates,
      })
      await load()
    } catch (e) {
      alert(paidRemovalExplanation(row.name, e) ?? (e instanceof Error ? e.message : 'Could not remove that parker.'))
    } finally { setBusy(false) }
  }

  const pending = rows.filter(r => r.status === 'pending')
  const decided = rows.filter(r => r.status !== 'pending')
  const emailEvents = useMemo(() => rows.flatMap(r => {
    const name = `${r.firstName} ${r.lastName}`
    return (r.emailEvents ?? []).map(event => ({
      id: `${r.id}:${event.id}`,
      at: event.at,
      text: emailEventLabel(name, event),
      recipient: event.recipient,
    }))
  }).sort((a, b) => b.at.localeCompare(a.at)), [rows])
  if (session.kind === 'device') return null

  // Pending registrations are requests, not parkers. Keep Total parkers aligned
  // with Austin's roster view and with Active; the pending tile is the separate
  // queue count and must not be double-counted into the roster total.
  const totalCount = activeCount
  // A failed read is unknown, not zero: its counts show "—" until a read
  // succeeds, so a manager is never told an empty roster or queue it may not have.
  const rosterUnknown = loading || !!rosterErr
  const queueUnknown = loading || !!err

  /**
   * Both exports write the roster AS FILTERED, not the whole thing — what you
   * see is what you get. `exportRows`/`exportHeader` are shared so CSV and PDF
   * can never disagree about the columns.
   *
   * The Zone column only appears for a multi-zone tenant, matching the table:
   * a single-zone export would repeat one lot name down every row.
   */
  const exportHeader = ['Name', 'Plate', 'Permit', ...(multiZone ? ['Zone'] : []), 'Phone', 'Email', 'Status']
  const exportRows = () => filteredRoster.map(r => [
    r.name,
    r.plates.join(', '),
    typeLabel(r.permitType || DEFAULT_PERMIT_TYPE),
    ...(multiZone ? [r.zoneIds.map(zoneName).join(', ')] : []),
    r.phone ?? '',
    r.email ?? '',
    STATUS_TONE[r.status].label,
  ])

  /** Names the filters in the file, so a partial export can't be mistaken for the whole roster. */
  const exportScopeLine = () => {
    const parts: string[] = []
    if (permitFilterLabel) parts.push(permitFilterLabel)
    else if (typeFilter) parts.push(`${typeLabel(typeFilter)} permits`)
    if (zoneFilter) parts.push(zoneName(zoneFilter))
    if (search.trim()) parts.push(`matching "${search.trim()}"`)
    return parts.length ? `Filtered: ${parts.join(' · ')}` : 'All parkers'
  }

  const canManageParkers = tenantCan(me, 'parkers.manage')
  const rosterGridColumns = [
    '32px',
    NAME_COL,
    'minmax(90px, 1fr)',
    'minmax(90px, .85fr)',
    ...(multiZone ? ['minmax(120px, 1fr)'] : []),
    'minmax(130px, 1fr)',
    `${STATUS_COL}px`,
    ...(canManageParkers ? [`${ACTION_COL}px`, '20px'] : []),
  ].join(' ')

  const exportCsv = () => {
    const lines = [exportHeader, ...exportRows()].map(cols => cols.map(csvCell).join(','))
    const blob = new Blob([lines.join('\n')], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = 'parkers.csv'
    a.click()
    URL.revokeObjectURL(url)
  }

  /**
   * PDF — a Parkify-branded roster a manager can hand to a landlord or file.
   * jsPDF + autotable are loaded ON DEMAND (dynamic import), exactly as ops's
   * Accounting.tsx does it: they're ~350KB and nothing about opening this
   * screen should pay for a button most visits never press. Body font is
   * Helvetica (jsPDF's default) for the same reason ops settled on it —
   * Avenir would mean embedding a TTF.
   */
  const exportPdf = async () => {
    const { default: JsPDF } = await import('jspdf')
    const autoTable = (await import('jspdf-autotable')).default
    const doc = new JsPDF({ orientation: 'portrait', unit: 'pt', format: 'letter' })
    const W = doc.internal.pageSize.getWidth()
    doc.setFillColor(127, 63, 152)
    doc.rect(0, 0, W, 54, 'F')
    doc.setTextColor(255, 255, 255)
    doc.setFont('helvetica', 'bold'); doc.setFontSize(18); doc.text('Parkify', 40, 34)
    doc.setFont('helvetica', 'normal'); doc.setFontSize(11); doc.text('Parker roster', 112, 34)
    doc.setTextColor(35, 31, 32); doc.setFont('helvetica', 'bold'); doc.setFontSize(10)
    doc.text(me.tenant.name, 40, 78)
    doc.setFont('helvetica', 'normal'); doc.setTextColor(120, 120, 120); doc.setFontSize(9)
    doc.text(`${exportScopeLine()}  ·  ${filteredRoster.length} ${filteredRoster.length === 1 ? 'parker' : 'parkers'}  ·  ${new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`, 40, 92)
    autoTable(doc, {
      startY: 108,
      head: [exportHeader],
      body: exportRows(),
      headStyles: { fillColor: [127, 63, 152] },
      styles: { fontSize: 9 },
    })
    doc.save('parkers.pdf')
  }

  const handlePdfExport = async () => {
    setExportOpen(false)
    setPdfExportError(null)
    setPdfExporting(true)
    try {
      await exportPdf()
    } catch (error) {
      // A Pages deployment replaces hashed assets. An already-open tab can
      // therefore request the previous jsPDF chunk when the manager first
      // clicks Export. Retrying that import in the same document is unreliable
      // because browsers cache failed module requests, so offer an explicit
      // reload instead of silently doing nothing or reloading mid-task.
      setPdfExportError(isChunkLoadError(error) ? 'stale-chunk' : 'failed')
      console.error('[tenant] PDF export', error)
    } finally {
      setPdfExporting(false)
    }
  }

  return (
    <div>
      {/* Total/Active/Pending — the roster's headline numbers, ahead of the
          mechanism (how the approve queue works) and the detail (the roster
          table itself). Pending requests are not parkers until approved; they
          belong only in the separate queue tile. */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatTile label="Total parkers" value={rosterUnknown ? '—' : totalCount} />
        <StatTile label="Active" value={rosterUnknown ? '—' : activeCount} color="#15803d" />
        <StatTile label="Pending approval" value={queueUnknown ? '—' : pending.length} color="#D97706" />
        <StatTile label="Registered vehicles" value={rosterUnknown ? '—' : rosterSummary.plateCount} />
      </div>

      {err && (
        <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 16 }}>
          <div style={{ flex: 1, minWidth: 220 }}>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load</div>
            <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{err}</div>
          </div>
          <button onClick={() => void load()} style={purpleBtn}>Retry</button>
        </div>
      )}

      {/* ── Waiting for approval ─────────────────────────────────────────── */}
      <div style={{ marginTop: 20 }}>
        {/* A titled card, not a bare micro-label. This block is the only thing
            on the screen with a deadline attached — nobody can park until it is
            answered — and the demo gives it a heading and a reason for existing
            rather than a filing label. The subtitle is the reason: it says what
            approving CHECKS, which is the one thing a manager cannot infer from
            a list of names. */}
        <div style={{ ...cardTitle, marginBottom: 3 }}>Waiting on you</div>
        <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.55, marginBottom: 10 }}>
          Registered through your sign-up link. Approving confirms they work here — the link is shareable, so this
          is the check that catches one that got out.
        </div>
        {err ? null : pending.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: 28, textAlign: 'center', fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)' }}>
            {loading ? '' : 'Nothing waiting. New requests show up here.'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {pending.map(r => (
              <div key={r.id} style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                {/* Orange, unlike the roster's name-hashed avatars: on this
                    block every row is a pending request, and the colour is what
                    says so at a glance instead of a status word per row. */}
                <Avatar name={`${r.firstName} ${r.lastName}`} tone="pending" />
                <div style={{ minWidth: 180, flex: '1 1 200px' }}>
                  <div style={{ fontSize: 14, fontWeight: 700 }}>{r.firstName} {r.lastName}</div>
                  <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>
                    Requested {fmtDate(r.createdAt)}{r.email ? ` · ${r.email}` : ''}{r.phone ? ` · ${r.phone}` : ''}
                  </div>
                  <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-gray-600)', marginTop: 2 }}>
                    {waitingLabel(r.createdAt)}
                    {(() => {
                      // The server lapses an unanswered request after 10 days
                      // (see lapseInfo for the legacy-row exception). Said here
                      // because "Waiting 8 days" alone never tells a manager
                      // the applicant is about to be told to start over.
                      const lapse = lapseInfo(r)
                      return lapse && (
                        <span data-testid="lapse-deadline" style={{ color: lapse.urgent ? '#b45309' : 'var(--color-gray-600)', fontWeight: lapse.urgent ? 800 : 700 }}>
                          {' · '}{lapse.label}
                        </span>
                      )
                    })()}
                  </div>
                  {/* Which bucket approving this will consume. Shown only when
                      the tenant HAS per-type caps — without them every request
                      lands in the same pool and the label is noise. */}
                  {seatTypes && (() => {
                    const t = r.permitType || DEFAULT_PERMIT_TYPE
                    // Room in THIS request's bucket. Said on the row itself
                    // because that is where the Approve button is: a manager
                    // reading "no spaces left" beside the button does not
                    // discover it by clicking and getting a server refusal.
                    // Absent pressure means UNPROVEN, not full — a type with
                    // no sub-cap is bounded by the overall ceiling instead, and
                    // flagging it here would assert a limit that doesn't exist.
                    // So the warning fires only on a figure we actually have.
                    const room = seatTypes.pressure?.[t]
                    const noRoom = !!room && room.left === 0
                    return (
                      // Sentence case and PURPLE by default, orange when
                      // blocked — the demo's treatment. Uppercase grey read as
                      // a column header for the row rather than a fact about
                      // the request, and red reads as an error when the benign
                      // case ("Staff permit") is the common one.
                      <div style={{
                        fontSize: 11.5, fontWeight: 800, marginTop: 4,
                        color: noRoom ? '#F16521' : '#7F3F98',
                      }}>
                        {typeLabel(t)} permit{noRoom ? ' · none left, can\u2019t be granted' : ''}
                      </div>
                    )
                  })()}
                </div>
                {/* Every plate the applicant submitted, not just the first: a
                    manager approving a two-car request must see both before
                    granting parking for both. */}
                <div data-testid="queue-plates" style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-start' }}>
                  {queuePlates(r).map(v => (
                    <code key={v.plate} style={{ fontFamily: 'ui-monospace, monospace', fontSize: 14, fontWeight: 700, letterSpacing: '1px', background: 'var(--color-gray-100)', borderRadius: 8, padding: '6px 12px' }}>{plateLabel(v.plate, v.state)}</code>
                  ))}
                </div>
                <div style={{ flex: 1 }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setDeclining(r)} style={ghostBtn}>Decline</button>
                  <button onClick={() => setApproving(r)} style={purpleBtn}>Approve</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {emailEvents.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Communication log</div>
          <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, overflow: 'hidden' }}>
            {emailEvents.map(event => (
              <div key={event.id} style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '11px 16px', borderBottom: '1px solid var(--color-gray-100)', flexWrap: 'wrap' }}>
                <span style={{ color: '#15803d', fontWeight: 900 }}>✓</span>
                <div style={{ flex: '1 1 260px', fontSize: 12.5, fontWeight: 700 }}>
                  {event.text} <span style={{ color: 'var(--color-gray-600)', fontWeight: 600 }}>· to {event.recipient}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── All parkers (the permit roster, flattened to one row per vehicle) ─
          The row opens a narrow detail editor. Authorization and enforcement
          fields stay read-only; this is for correcting the tenant's own roster
          details, not changing who is allowed to park. */}
      <div style={{ marginTop: 26 }}>
        {/* Keep the section controls visually separate from the table. When a
            roster has only one permit type the chips row is absent, so this
            spacing is the only breathing room between the heading/filters and
            the table header. */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, flexWrap: 'wrap' }}>
          <div style={labelStyle}>All parkers</div>
          <div style={{ flex: 1 }} />
          {/* A <select>, not chips, for zones: a tenant's lot count is
              unbounded (chips would wrap into soup), while permit types are a
              fixed set of five and read better as chips. */}
          {zoneCanNarrow && (
            <select
              value={zoneFilter ?? ''}
              onChange={e => setZoneFilter(e.target.value || null)}
              aria-label="Filter by zone"
              style={selectStyle}
            >
              <option value="">All zones</option>
              {me.zones.map(z => <option key={z.id} value={z.id}>{z.displayName}</option>)}
            </select>
          )}
          <input
            value={search} onChange={e => setSearch(e.target.value)} placeholder="Search name, email or plate"
            style={{ fontSize: 13, padding: '7px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontFamily: 'inherit', width: 200 }}
          />
          {canManageParkers && <AddParkerActions token={session.token} namedPermits={namedPermits} onAddManually={() => setAdding(true)} />}
          {tenantCan(me, 'parkers.export') && (
            <div style={{ position: 'relative' }}>
              <button
                onClick={() => setExportOpen(open => !open)}
                disabled={filteredRoster.length === 0 || pdfExporting}
                style={{ ...ghostBtn, opacity: filteredRoster.length === 0 || pdfExporting ? 0.5 : 1 }}
              >{pdfExporting ? 'Creating PDF…' : 'Export'}</button>
              {exportOpen && filteredRoster.length > 0 && (
                <div style={{ position: 'absolute', top: 'calc(100% + 6px)', right: 0, zIndex: 20, minWidth: 172, padding: 5, background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 10, boxShadow: '0 12px 30px rgba(0,0,0,0.14)' }}>
                  <button onClick={() => { setExportOpen(false); exportCsv() }} style={{ display: 'block', width: '100%', padding: '9px 11px', border: 0, borderRadius: 7, background: 'transparent', textAlign: 'left', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Download CSV</button>
                  <button onClick={() => void handlePdfExport()} style={{ display: 'block', width: '100%', padding: '9px 11px', border: 0, borderRadius: 7, background: 'transparent', textAlign: 'left', fontSize: 12.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>Download PDF</button>
                </div>
              )}
            </div>
          )}
        </div>

        {pdfExportError && (
          <div role="alert" style={{ background: 'rgba(245,158,11,0.10)', border: '1.5px solid rgba(245,158,11,0.38)', borderRadius: 12, padding: '12px 14px', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 14 }}>
            <div style={{ flex: 1, minWidth: 220, fontSize: 13, fontWeight: 650, color: 'var(--color-gray-800)', lineHeight: 1.5 }}>
              {pdfExportError === 'stale-chunk'
                ? 'Parkify was updated while this tab was open. Reload to download the PDF.'
                : 'Couldn\u2019t create the PDF. Your roster is unchanged; try the export again.'}
            </div>
            {pdfExportError === 'stale-chunk' ? (
              <button onClick={() => window.location.reload()} style={purpleBtn}>Reload Parkify</button>
            ) : (
              <button onClick={() => void handlePdfExport()} style={purpleBtn}>Try PDF again</button>
            )}
          </div>
        )}

        {/* Permit-type chips. Rendered whenever the roster spans more than one
            type — NOT gated on `seatTypes` (per-type caps): a tenant with mixed
            permit types and no sub-caps configured still benefits from
            filtering, and gating on caps would hide the filter from them. */}
        {/* `|| typeFilter` guarantees an escape hatch: a drill-through can
            arrive pre-filtered to a type the roster has no rows for, and
            without the chips there'd be no way to clear it. */}
        {!rosterErr && (typeCounts.size > 1 || typeFilter !== null) && (
          <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            <TypeChip
              label="All" count={searchZoneFiltered.length}
              active={typeFilter === null} onClick={() => { setTypeFilter(null); setPermitFilter(null) }}
            />
            {[...typeCounts.entries()]
              .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
              .map(([type, count]) => (
                <TypeChip
                  key={type} label={typeLabel(type)} count={count}
                  active={typeFilter === type} onClick={() => { setTypeFilter(typeFilter === type ? null : type); setPermitFilter(null) }}
                />
              ))}
          </div>
        )}
        {/* Named permits inside the type: "Employee: All · Canvas Properties ·
            Otto's". A second row rather than more type chips, because these
            are lists WITHIN a type and the type chip above still means what it
            says. Counts follow search, zone and type, like the chips above. */}
        {permitPickerShown && (
          <div role="group" aria-label="Filter by permit" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--color-gray-600)', marginRight: 2 }}>
              {permitScopeType ? `${typeLabel(permitScopeType)}:` : 'Permit:'}
            </span>
            <TypeChip
              label="All" count={typeFiltered.length}
              active={permitFilter === null} onClick={() => setPermitFilter(null)}
            />
            {permitOptions.map(o => (
              <TypeChip
                key={o.id} label={o.label} count={o.count}
                active={permitFilter === o.id} onClick={() => setPermitFilter(permitFilter === o.id ? null : o.id)}
              />
            ))}
          </div>
        )}
        {rosterErr ? (
          <div style={{ background: 'rgba(237,28,36,0.10)', border: '1.5px solid rgba(237,28,36,0.4)', borderRadius: 12, padding: '14px 16px', display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: '#b91c1c', marginBottom: 2 }}>Couldn't load your roster</div>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-700)', lineHeight: 1.5 }}>{rosterErr}</div>
            </div>
            <button onClick={() => void load()} style={purpleBtn}>Retry</button>
          </div>
        ) : filteredRoster.length === 0 ? (
          <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: 28, textAlign: 'center', fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)' }}>
            {loading ? '' : roster.length === 0 ? 'No parkers yet — approve a request above, or ask your operator.' : 'No parkers match those filters.'}
          </div>
        ) : (
          <div className="tenant-roster" style={{ background: '#fff', borderRadius: 12, overflowX: 'auto', border: '1px solid var(--color-gray-200)' }}>
            <div className="tenant-roster-header" style={{ display: 'grid', width: '100%', gridTemplateColumns: rosterGridColumns, alignItems: 'center', columnGap: 12, padding: '10px 16px', borderBottom: '1px solid var(--color-gray-100)' }}>
              <div />
              <div style={{ ...labelStyle, marginBottom: 0 }}>Name</div>
              <div style={{ ...labelStyle, marginBottom: 0 }}>License plate</div>
              <div style={{ ...labelStyle, marginBottom: 0 }}>Permit</div>
              {multiZone && <div style={{ ...labelStyle, marginBottom: 0 }}>Zone</div>}
              <div style={{ ...labelStyle, marginBottom: 0 }}>Phone</div>
              {/* FIXED, not `0 0 auto` — see the row's own note. */}
              <div style={{ ...labelStyle, marginBottom: 0 }}>Status</div>
              {canManageParkers && <div style={{ ...labelStyle, marginBottom: 0 }}>Actions</div>}
              {canManageParkers && <div aria-hidden="true" />}
            </div>
            {filteredRoster.map(r => {
              const tone = STATUS_TONE[r.status]
              const editable = canManageParkers
              const openEditor = () => { if (editable) setEditing(r) }
              return (
                /* ROW-PATTERN-001 (demo #106 / #103): the row stays the pointer
                   target; the name is the keyboard target, a real button named
                   "<name>, edit parker". The row used to be role="button" with
                   an Enter/Space handler and nested buttons inside it, so Enter
                   on Remove bubbled up and opened Edit instead. */
                <div
                  key={r.key}
                  className="tenant-roster-row"
                  onClick={openEditor}
                  style={{ display: 'grid', width: '100%', gridTemplateColumns: rosterGridColumns, alignItems: 'center', columnGap: 12, padding: '12px 16px', borderBottom: '1px solid var(--color-gray-100)', cursor: editable ? 'pointer' : 'default' }}
                >
                  <Avatar name={r.name} />
                  <div className="tenant-roster-identity" style={{ minWidth: 0 }}>
                    {editable
                      ? <RowOpen text={r.name} action="edit parker" onOpen={openEditor}><span style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</span></RowOpen>
                      : <div style={{ fontSize: 13, fontWeight: 600 }}>{r.name}</div>}
                    {r.email && (
                      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.email}</div>
                    )}
                  </div>
                  {/* Every plate this parker holds, not just the first — a row
                      now stands for the whole parker, and a manager reading
                      only one plate off a two-car employee's row would not
                      know the second car is covered by the same permit.
                      Stacked one-per-line (the demo's treatment, parkify-demo
                      -v2-wip.jsx:16701-16704): joined inline with " · " read
                      as clutter once a parker had three plates, and it also
                      hid which plate carried which state. A one-plate row
                      renders exactly as it did before — a single line, same
                      font/weight/color the cell already used. */}
                  <div
                    className="tenant-roster-detail tenant-roster-plates" data-label="License plate"
                    title={r.plates.join(', ')}
                    style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', minWidth: 0 }}
                  >
                    {r.parkerVehicles.map((v, i) => (
                      <div key={v.plate} style={{ marginTop: i ? 2 : 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {plateLabel(v.plate, v.state, v.stateUnknown)}
                      </div>
                    ))}
                  </div>
                  <div className="tenant-roster-detail" data-label="Permit" style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>{typeLabel(r.permitType || DEFAULT_PERMIT_TYPE)}</div>
                  {/* A permit can cover several lots. Naming the first and
                      counting the rest keeps the column one line without
                      implying the plate is only good in one place.
                      The "+N" is its OWN non-shrinking element: folded into the
                      same string it gets eaten by the ellipsis on any long lot
                      name, which silently turns a multi-zone permit into a
                      single-zone one. Full list in the title attribute. */}
                  {multiZone && (
                    <div
                      className="tenant-roster-detail tenant-roster-zone" data-label="Zone"
                      title={r.zoneIds.length ? r.zoneIds.map(zoneName).join(', ') : undefined}
                      style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', minWidth: 0, display: 'flex', alignItems: 'baseline', gap: 4 }}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {r.zoneIds.length === 0 ? '—' : zoneName(r.zoneIds[0])}
                      </span>
                      {r.zoneIds.length > 1 && (
                        <span style={{ flex: '0 0 auto', fontWeight: 700 }}>+{r.zoneIds.length - 1}</span>
                      )}
                    </div>
                  )}
                  <div className="tenant-roster-detail" data-label="Phone" style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>{r.phone || '—'}</div>
                  {/* ⚠️ FIXED-WIDTH CELL, pill sized to its own text inside it.
                      This column used to be `flex: '0 0 auto'` on the pill
                      itself, so it sized to its LABEL — and the labels differ:
                      measured at 1512px, "ACTIVE" is 68px and "ENDING SOON" is
                      112px. The 44px difference came out of the flexible Name
                      cell (`1 1 160px`), which dragged plate/permit/phone 44px
                      left on exactly the rows with a longer status. One
                      "Ending soon" permit visibly broke the whole table's
                      alignment. A fixed basis makes the label irrelevant to
                      layout; widen STATUS_COL if a longer label is ever added. */}
                  <div className="tenant-roster-status" style={{ display: 'flex' }}>
                    <div>
                      <span style={{
                        display: 'inline-block', fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px',
                        padding: '4px 10px', borderRadius: buttonRadius,
                        background: tone.bg, color: tone.fg, border: `1px solid ${tone.border}`,
                      }}>
                        {tone.label}
                      </span>
                      <div style={{ fontSize: 10.5, fontWeight: 700, color: r.status === 'expiringSoon' ? '#92400e' : 'var(--color-gray-600)', marginTop: 3, whiteSpace: 'nowrap' }}>
                        {permitStartLabel(r.startDate) && <div>{permitStartLabel(r.startDate)}</div>}
                        {permitEndLabel(r.status, r.validUntil)}
                        {r.parkerVehicles.find(v => v.noticeBilledThrough) && ` · billed through ${friendlyDate(r.parkerVehicles.find(v => v.noticeBilledThrough)!.noticeBilledThrough!)}`}
                      </div>
                    </div>
                  </div>
                  {editable && (
                    <div className="tenant-roster-actions">
                    <RowActions style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                      {/* Program seats carry the operator's terms; details are edited from the Console. Remove still works and releases the seat. */}
                      {r.programIssuanceId
                        ? <span title="Shared-program seat — your parking operator edits its details" style={{ fontSize: 11, fontWeight: 700, color: 'var(--color-gray-600)', alignSelf: 'center' }}>Program seat{r.bayIds?.length ? ` · Bay ${r.bayIds.map(id => bayLabel.get(id) ?? id).join(', ')}` : ''}</span>
                        : <button type="button" onClick={() => setEditing(r)} aria-label={`Edit ${r.name}`} style={tableActionBtn}>Edit</button>}
                      <button type="button" onClick={() => void handleRemove(r)} disabled={busy} aria-label={`Remove ${r.name}`} style={tableRemoveBtn}>Remove</button>
                    </RowActions>
                    </div>
                  )}
                  {editable && <span className="tenant-roster-cue"><RowCue /></span>}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* ── Decided ──────────────────────────────────────────────────────── */}
      {decided.length > 0 && (
        <div style={{ marginTop: 26 }}>
          <div style={{ ...labelStyle, marginBottom: 8 }}>Closed requests</div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginBottom: 8 }}>
            Lapsed and declined requests are not in your roster. A lapsed request can be submitted again; a declined request cannot.
          </div>
          <div style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid var(--color-gray-200)' }}>
            {decided.map(r => (
              <div className="tenant-closed-request" key={r.id} style={{ display: 'grid', gridTemplateColumns: '1fr 130px 120px 110px', alignItems: 'center', padding: '12px 16px', borderBottom: '1px solid var(--color-gray-100)', fontSize: 13, gap: 8 }}>
                <div style={{ fontWeight: 600 }}>{r.firstName} {r.lastName}</div>
                <code style={{ fontFamily: 'ui-monospace, monospace', fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>{r.plate}</code>
                <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>
                  {r.status === 'lapsed' ? 'Lapsed after 10 days with no answer — they can register again' : fmtDate(r.decidedAt)}
                </div>
                <div style={{
                  fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.5px',
                  color: r.status === 'approved' ? '#15803d' : 'var(--color-gray-600)', textAlign: 'right',
                }}>
                  {r.status === 'lapsed' ? 'Lapsed' : r.status}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {approving && (
        <ApproveModal
          reg={approving}
          billing={hideMonetaryValues ? null : billing}
          busy={busy}
          onCancel={() => setApproving(null)}
          onConfirm={(validUntil, plateStates, bayId, permitId) => void handleApprove(approving, validUntil, plateStates, bayId, permitId)}
          namedPermits={approving.programId ? null : namedPermits}
          pool={(programs ?? []).find(p => p.id === approving.programId)?.bayPool?.filter(b => !b.issuanceId && b.state === 'active') ?? []}
        />
      )}

      {declining && (
        // "…and they aren't told" stopped being true in #2155: declining now emails
        // the applicant. It carries no reason, because this screen doesn't collect
        // one — so say exactly that rather than implying either silence or an
        // explanation we don't send.
        <TenantModal
          title="Decline request"
          sub={`Decline ${declining.firstName} ${declining.lastName} (${declining.plate})? They won't be able to park. We'll email them that the request was declined, without a reason.`}
          onClose={() => setDeclining(null)}
          footer={<>
            <button type="button" onClick={() => setDeclining(null)} style={ghostBtn}>Cancel</button>
            <button type="button" onClick={() => void handleDecline(declining)} style={dangerBtn}>Decline request</button>
          </>}
        />
      )}

      {removing && (
        <TenantModal
          title="Remove parker"
          sub={`Remove ${removing.name} (${removing.plate}) from this tenant's free-parking roster? They will no longer be covered by this permit.`}
          onClose={() => setRemoving(null)}
          footer={<>
            <button type="button" onClick={() => setRemoving(null)} style={ghostBtn}>Cancel</button>
            <button type="button" onClick={() => void removeFromFreeRoster(removing)} style={dangerBtn}>Remove parker</button>
          </>}
        />
      )}

      {adding && (
        <AddParkerModal
          busy={busy}
          permitTypes={addParkerPermitTypes}
          namedPermits={namedPermits}
          platesPerParker={platesPerParker}
          onCancel={() => setAdding(false)}
          onConfirm={parker => void handleAddParker(parker)}
        />
      )}
      {editing && (
        <EditParkerModal
          row={editing}
          busy={busy}
          platesPerParker={editing.platesPerParker ?? platesPerParker}
          permitTypes={addParkerPermitTypes}
          paid={paidMonthly}
          suggestedEnd={suggestedEnd}
          previewNotice={validUntil => previewPaidNotice(session.token, editing.permitId, editing.plate, validUntil)}
          onCancel={() => { setEditing(null); setSuggestedEnd(undefined) }}
          onConfirm={detail => void handleEdit(editing, detail)}
        />
      )}
    </div>
  )
}

interface EditParkerDetail {
  name: string
  description?: string
  phone?: string
  email?: string
  vehicleEdits: TenantVehicleEdit[]
  permitType?: string
  validUntil: string
}

/**
 * The server refused an edit that would end a paid parker's seat outright: their
 * last plate removed, or a name, email or phone that makes them the same parker
 * as someone else (two parkers, one seat). Says so, with the server's own dates
 * on the lot clock. Null for any other error, which is shown as the server wrote it.
 */
export function paidRemovalExplanation(name: string, e: unknown): string | null {
  const err = e as { code?: string; body?: { endFrom?: unknown; billedThroughIfEndedToday?: unknown } } | null
  if (!err || err.code !== PAID_REMOVAL_REFUSED_CODE) return null
  const endFrom = typeof err.body?.endFrom === 'string' ? err.body.endFrom : undefined
  const billed = typeof err.body?.billedThroughIfEndedToday === 'string' ? err.body.billedThroughIfEndedToday : undefined
  return `This change would end ${name}'s paid seat: it removes their last plate, or gives them the same name, email or phone as another parker, which counts the two as one seat. `
    + `On a paid monthly plan, end a parker by setting an end date instead`
    + (endFrom ? `: any date from ${friendlyDate(endFrom)} on` : '')
    + (billed ? `, billed through ${friendlyDate(billed)} if access ends that day.` : '.')
}

/** The pop-up a paying tenant confirms when it ends a parker inside the 30-day notice period. */
export function endEarlyConfirmText(name: string, endDate: string, billedThrough: string): string {
  return `${name}'s access ends after ${friendlyDate(endDate)}. Your plan bills ${PAID_NOTICE_DAYS} days' notice, so you'll still be billed for this parker through ${friendlyDate(billedThrough)}. Continue?`
}

/** The default plates per parker (api permits/plates_per_parker.ts); the location may set another. */
const DEFAULT_PLATES_PER_PARKER = 3

/**
 * Read-only: the limit is set on the location by the operator, so the manager
 * is told it, never offered it. A person already over it (added before the
 * limit existed) keeps their plates; only adding another is refused.
 */
function PlateLimitNote({ limit, count }: { limit?: number; count: number }) {
  const n = limit ?? DEFAULT_PLATES_PER_PARKER
  return (
    <div data-testid="plate-limit-note" style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 6, lineHeight: 1.5 }}>
      Up to {n} plate{n === 1 ? '' : 's'} per parker at this location, set by your operator.
      {count > n && <> This parker already has {count}; they keep them, but no more can be added.</>}
    </div>
  )
}

function EditParkerModal({
  row, busy, platesPerParker, permitTypes, paid, suggestedEnd, previewNotice, onCancel, onConfirm,
}: {
  row: RosterRow
  busy: boolean
  /** Read-only, from the location. Absent = unread: fall back to the default, the server still decides. */
  platesPerParker?: number
  /** Types this tenant already holds: the same list Add parker offers. The API refuses any other. */
  permitTypes: string[]
  /** The tenant pays for monthly parkers: billing runs 30 days from the notice, whatever end date it picks. */
  paid: boolean
  /** Opened from Remove on a paying tenant: the end date starts 30 days out. */
  suggestedEnd?: string
  /** The server's billed-through date for an end date, on the lot clock. Every billed date shown comes from here. */
  previewNotice: (validUntil: string) => Promise<PaidNoticePreview>
  onCancel: () => void
  onConfirm: (detail: EditParkerDetail) => void
}) {
  const [name, setName] = useState(row.name === row.plate ? '' : row.name)
  const [description, setDescription] = useState(row.description ?? '')
  // Shown formatted; a stored value this rule cannot read (a pre-2026 row with
  // seven digits, say) is shown as stored and reported under the field.
  const [phone, setPhone] = useState(formatStoredParkerPhone(row.phone))
  const [email, setEmail] = useState(row.email ?? '')
  const [vehicleEdits, setVehicleEdits] = useState<TenantVehicleEdit[]>(row.parkerVehicles.map(vehicle => ({
    originalPlate: vehicle.plate,
    plate: vehicle.plate,
    state: plateStateChoice(vehicle),
  })))
  const [permitType, setPermitType] = useState(row.permitType ?? DEFAULT_PERMIT_TYPE)
  const [validUntil, setValidUntil] = useState(suggestedEnd ?? row.validUntil)
  const [noEndDate, setNoEndDate] = useState(!(suggestedEnd ?? row.validUntil))
  const inputStyle: CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)',
    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
  }
  const cleanVehicleEdits = vehicleEdits.map(edit => plateStateInput({
    ...(edit.originalPlate ? { originalPlate: edit.originalPlate } : {}),
    plate: edit.plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase(),
    state: edit.state,
  }))
  const cleanPlates = cleanVehicleEdits.map(edit => edit.plate)
  const phoneDigits = normalizeParkerPhone(phone)
  const valid = name.trim().length > 0 && phoneDigits !== null && (!email.trim() || email.includes('@'))
    && cleanPlates.length > 0 && cleanPlates.every(plate => plate.length >= 2 && plate.length <= 12)
    && new Set(cleanPlates).size === cleanPlates.length
    && vehicleEdits.every(edit => isPlateStateChoice(edit.state))
    && endDateAllowed({ paid, noEndDate, validUntil, original: row.validUntil })
  // Only a CHANGED end date is billed from a new notice (the server stamps it
  // only then), so an unrelated edit to a parker already inside the window asks
  // nothing. The billed-through date is the server's, on the lot clock.
  const endChanged = paid && !noEndDate && !!validUntil && validUntil !== row.validUntil
  const [preview, setPreview] = useState<{ validUntil: string; billedThrough: string | null } | null>(null)
  const [checking, setChecking] = useState(false)
  // The parent passes a fresh closure each render; read it through a ref so the
  // preview is asked once per end date, not once per render.
  const previewRef = useRef(previewNotice)
  previewRef.current = previewNotice
  useEffect(() => {
    if (!endChanged) return
    let live = true
    previewRef.current(validUntil)
      .then(p => { if (live) setPreview({ validUntil, billedThrough: p.paysMonthly ? p.billedThrough : null }) })
      .catch(() => { if (live) setPreview(null) })
    return () => { live = false }
  }, [endChanged, validUntil])
  const billedPreview = endChanged && preview?.validUntil === validUntil ? preview.billedThrough ?? undefined : undefined

  const save = async () => {
    const detail = { name: name.trim(), description: description.trim(), phone: phoneDigits ?? '', email: email.trim(), vehicleEdits: cleanVehicleEdits, permitType, validUntil: noEndDate ? '' : validUntil }
    if (!endChanged) { onConfirm(detail); return }
    // Maecy, 2026-10-16: ending a paid parker early must say, before it is
    // saved, the exact date the tenant is still billed through. Asked fresh, so
    // the date in the confirm is the one the save will stamp.
    setChecking(true)
    let answer: PaidNoticePreview
    try { answer = await previewNotice(validUntil) }
    catch { alert("Couldn't check the billing date for this end date — try again."); return }
    finally { setChecking(false) }
    const billed = answer.paysMonthly ? answer.billedThrough : null
    if (billed && !confirm(endEarlyConfirmText(name.trim() || row.name, validUntil, billed))) return
    onConfirm(detail)
  }

  return (
    <div onClick={onCancel} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={modal}>
        <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>Edit parker</h3>
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)', margin: '0 0 16px', lineHeight: 1.55 }}>
          Update this parker’s details, plates, permit type, or end date.
        </p>

        <div style={modalSection}>
          <div style={labelStyle}>Name</div>
          <input aria-label="Name" autoFocus value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
        </div>
        <div style={modalSection}>
          <div style={labelStyle}>License plates</div>
          {vehicleEdits.map((vehicle, index) => (
            <div className="plate-state-row" key={vehicle.originalPlate ?? `new-${index}`} style={{ gap: 8, alignItems: 'start', marginBottom: 10 }}>
              <div>
                <div style={labelStyle}>Plate {index + 1}</div>
                <input aria-label={`Plate ${index + 1}`} value={vehicle.plate} onChange={e => setVehicleEdits(prev => prev.map((item, i) => i === index ? { ...item, plate: e.target.value.toUpperCase() } : item))} style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', letterSpacing: '1px' }} />
              </div>
              <div>
                <div style={labelStyle}>State / province</div>
                <select aria-label={`State / province for plate ${index + 1}`} value={vehicle.state} onChange={e => setVehicleEdits(prev => prev.map((item, i) => i === index ? { ...item, state: e.target.value } : item))} style={inputStyle}>
                  <option value="">Select a state</option>
                  <option value={PLATE_STATE_UNKNOWN}>{PLATE_STATE_UNKNOWN_LABEL}</option>
                  {PLATE_STATE_GROUPS.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map(option => <option key={option.code} value={option.code}>{option.label}</option>)}
                    </optgroup>
                  ))}
                </select>
                {!isPlateStateChoice(vehicle.state) && <div style={{ fontSize: 12, color: '#b91c1c', marginTop: 5 }}>Required for this plate. Choose State unknown if the client did not say.</div>}
              </div>
              {vehicleEdits.length > 1 && <button className="plate-state-remove" type="button" aria-label={`Remove plate ${index + 1}`} onClick={() => setVehicleEdits(prev => prev.filter((_, i) => i !== index))} style={{ ...ghostBtn, marginTop: 20 }}>Remove</button>}
            </div>
          ))}
          {vehicleEdits.length < (platesPerParker ?? DEFAULT_PLATES_PER_PARKER) && <button type="button" onClick={() => setVehicleEdits(prev => [...prev, { plate: '', state: '' }])} style={{ ...ghostBtn, marginTop: 2 }}>+ Add another plate</button>}
          <PlateLimitNote limit={platesPerParker} count={vehicleEdits.length} />
        </div>
        <div style={modalSection}>
          <div style={labelStyle}>Vehicle details (optional)</div>
          <input aria-label="Vehicle details" value={description} onChange={e => setDescription(e.target.value)} placeholder="Make, model, or color" style={inputStyle} />
        </div>
        <div style={{ ...modalSection, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 180px' }}>
            <ParkerPhoneField value={phone} onChange={setPhone} inputStyle={inputStyle} />
          </div>
          <div style={{ flex: '1 1 180px' }}>
            <div style={labelStyle}>Email (optional)</div>
            <input aria-label="Email" type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={modalSection}>
          <div style={labelStyle}>Permit type</div>
          <select value={permitType} onChange={e => setPermitType(e.target.value)} style={inputStyle}>
            {Object.entries(TYPE_LABEL)
              .filter(([type]) => permitTypes.includes(type) || type === (row.permitType ?? DEFAULT_PERMIT_TYPE))
              .map(([type, label]) => <option key={type} value={type}>{label}</option>)}
          </select>
        </div>
        <div style={modalSection}>
          <div style={labelStyle}>Approved through</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            <input type="checkbox" checked={noEndDate} onChange={e => { setNoEndDate(e.target.checked); if (e.target.checked) setValidUntil('') }} />
            No end date
          </label>
          {!noEndDate && <DatePicker value={validUntil} min={todayCalendarDate()} ariaLabel="Permit end date" onChange={date => setValidUntil(date ?? '')} buttonStyle={inputStyle} />}
          <div style={{ fontSize: 12, color: 'var(--color-gray-600)', marginTop: 5 }}>
            {paid
              ? (billedPreview
                ? `Access ends on the date you pick. Your plan bills ${PAID_NOTICE_DAYS} days' notice, so you're billed through ${friendlyDate(billedPreview)}.`
                : `Access ends on the date you pick. Your plan bills ${PAID_NOTICE_DAYS} days' notice; an end date ${PAID_NOTICE_DAYS}+ days out is billed through that date.`)
              : 'No end date means you are responsible for removing this parker when they leave.'}
          </div>
        </div>

        <div style={modalActions}>
          <button onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button
            onClick={() => void save()}
            disabled={busy || checking || !valid}
            style={{ ...purpleBtn, opacity: busy || checking || !valid ? 0.6 : 1, cursor: busy || checking || !valid ? 'default' : 'pointer' }}
          >
            {busy || checking ? 'Saving…' : 'Save details'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * The confirm step. It names the grant — plate, dates, the 24/7 caveat, and
 * whether the space is billable — because "Approve?" doesn't tell a manager
 * what they're about to hand out.
 *
 * The billing line is stated BEFORE access is granted, on the screen where the
 * decision is made, rather than left to arrive on an invoice weeks later. It
 * names counts and never an amount: the server sends no rate, and a figure
 * invented here would be a fabricated number in front of a billing decision.
 */
function ApproveModal({
  reg, billing, busy, onCancel, onConfirm, pool, namedPermits,
}: {
  reg: TenantRegistration
  billing: TenantPermitBilling | null
  busy: boolean
  onCancel: () => void
  onConfirm: (validUntil: string, plateStates: PlateStateInput[], bayId?: string | null, permitId?: string | null) => void
  /** Named permits: approving puts the parker on the one picked here. */
  namedPermits?: NamedPermitOption[] | null
  /** Free reserved bays in this tenant's pool for the request's program (empty when none). */
  pool?: { bayId: string; label: string; section: string | null }[]
}) {
  const sentence = billingSentence(billing)
  const [bayId, setBayId] = useState('')
  const [permitId, setPermitId] = useState(
    namedPermits?.some(p => p.id === reg.targetPermitId) ? reg.targetPermitId! : '')
  const billable = billing?.next.billable ?? false
  const [validUntil, setValidUntil] = useState('')
  // A shared-program request takes one seat from your allocation and has no
  // end date by the program's terms, so the date controls never apply to it.
  const programRequest = !!reg.programId
  const [noEndDate, setNoEndDate] = useState(true)
  const today = todayCalendarDate()

  // Plates this request never named a jurisdiction for. Approving mints a
  // permit and the server requires one per plate, so without asking here the
  // manager gets a refusal they cannot act on from this queue. Registrations
  // submitted through today's form arrive complete and show nothing extra.
  const missingStates = useMemo(() => {
    const known = new Map((reg.plateStates ?? []).map(v => [v.plate, v.state]))
    return (reg.plates?.length ? reg.plates : [reg.plate])
      .filter((plate): plate is string => !!plate)
      .filter(plate => !isPlateStateCode(known.get(plate) ?? ''))
      .map(plate => ({ plate, state: '' }))
  }, [reg])
  const [supplied, setSupplied] = useState<PlateStateInput[]>(missingStates)

  const statesAnswered = supplied.every(v => isPlateStateCode(v.state))
  const valid = (programRequest || noEndDate || (/^\d{4}-\d{2}-\d{2}$/.test(validUntil) && validUntil >= today))
    && statesAnswered
    && (!namedPermits || !!permitId)

  return (
    <div onClick={onCancel} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={modal}>
        <h3 style={{ fontSize: 18, fontWeight: 800, margin: '0 0 6px' }}>
          Give {reg.firstName} {reg.lastName} free parking?
        </h3>
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)', margin: '0 0 16px', lineHeight: 1.55 }}>
          Plate <b style={{ fontFamily: 'ui-monospace, monospace' }}>{reg.plate}</b> parks free in your zones from
          today through the date below — <b>24 hours a day</b>, not only on shift. If they should only park during
          set hours, your parking operator sets that up; this form can't.
        </p>

        {/* Billable spaces get the warning treatment; an included one is a
            plain note. Nothing renders at all under an `included` lease or an
            unset model — an inapplicable row is hidden, not greyed out. */}
        {sentence && (
          <div style={{
            borderRadius: 10, padding: '12px 14px', margin: '0 0 16px', fontSize: 13, lineHeight: 1.55,
            background: billable ? 'rgba(245,158,11,0.10)' : 'var(--color-gray-100)',
            border: `1px solid ${billable ? 'rgba(245,158,11,0.35)' : 'var(--color-gray-200)'}`,
            color: billable ? '#92400e' : 'var(--color-gray-700)',
          }}>
            <b>{billable ? 'This space is billable.' : 'Included in your lease.'}</b>{' '}
            {sentence}
          </div>
        )}

        {programRequest && (
          <div style={{ ...modalSection, fontSize: 13, fontWeight: 600, lineHeight: 1.55, color: 'var(--color-gray-700)' }}>
            <b>Shared program: {reg.programName ?? 'name unavailable'}.</b> Approving takes one of your program seats for this parker
            and all their vehicles. Program parking has no end date; remove the parker when they leave.
            {reg.reservedBay && (
              <div style={{ marginTop: 8, color: '#7F3F98' }}>
                <b>They signed up through your reserved-bay link.</b>{' '}
                {(pool?.length ?? 0) > 0
                  ? 'Pick their bay below; approving without one gives a general seat instead.'
                  : 'No bay in your pool is free right now — approving gives a general seat, or decline and ask your operator for more bays.'}
              </div>
            )}
            {(pool?.length ?? 0) > 0 && (
              <label style={{ display: 'grid', gap: 4, marginTop: 10, fontWeight: 700 }}>
                Reserved bay (optional)
                <select aria-label="Reserved bay" value={bayId} onChange={e => setBayId(e.target.value)} disabled={busy}
                  style={{ fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-gray-300)', fontFamily: 'inherit' }}>
                  <option value="">No reserved bay — general seat</option>
                  {pool!.map(b => <option key={b.bayId} value={b.bayId}>{b.label}{b.section ? ` (${b.section})` : ''}</option>)}
                </select>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)' }}>{pool!.length} of your pool bays {pool!.length === 1 ? 'is' : 'are'} free. The bay is taken with the seat and released when the parker is removed.</span>
              </label>
            )}
          </div>
        )}
        {namedPermits && (
          <div style={modalSection}>
            <div style={labelStyle}>Permit</div>
            <select aria-label="Permit" value={permitId} onChange={e => setPermitId(e.target.value)} disabled={busy}
              style={{ width: '100%', fontSize: 13, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-gray-300)', fontFamily: 'inherit' }}>
              <option value="">Choose a permit</option>
              {namedPermits.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
        )}
        <div style={modalSection} hidden={programRequest}>
          <div style={labelStyle}>Approved through</div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
            <input type="checkbox" checked={noEndDate} onChange={e => { setNoEndDate(e.target.checked); if (e.target.checked) setValidUntil('') }} />
            No end date
          </label>
          {!noEndDate && <DatePicker value={validUntil} min={today} ariaLabel="Permit end date" onChange={date => setValidUntil(date ?? '')} buttonStyle={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)', fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }} />}
          <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', margin: '6px 0 0', lineHeight: 1.5 }}>
            No end date means you are responsible for removing this parker when they leave.
          </p>
        </div>

        {/* Only for requests that predate the jurisdiction field, or arrived
            through a client that never sent it. Asked HERE because approving
            is what needs it — the alternative is a refusal the manager has no
            way to resolve from this screen. */}
        {supplied.length > 0 && (
          <div style={{ marginTop: 16 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--color-gray-900)', marginBottom: 4 }}>
              Which state or province issued {supplied.length > 1 ? 'these plates' : 'this plate'}?
            </div>
            <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', margin: '0 0 10px', lineHeight: 1.5 }}>
              This request came in before we asked for it. Enforcement needs it to tell the plate
              apart from the same number registered in another state.
            </p>
            {supplied.map((vehicle, index) => (
              <div key={vehicle.plate} style={{ display: 'flex', gap: 8, alignItems: 'center', marginTop: index ? 8 : 0 }}>
                <span style={{ fontFamily: 'ui-monospace, monospace', fontWeight: 700, minWidth: 96 }}>{vehicle.plate}</span>
                <select
                  aria-label={`${vehicle.plate} state / province`}
                  value={vehicle.state}
                  onChange={e => setSupplied(prev => prev.map((v, i) => i === index ? { ...v, state: e.target.value } : v))}
                  style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: '1px solid var(--color-gray-300)' }}
                >
                  <option value="">Select…</option>
                  {PLATE_STATE_GROUPS.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map(option => <option key={option.code} value={option.code}>{option.label}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            ))}
          </div>
        )}

        <div style={modalActions}>
          <button onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button
            onClick={() => onConfirm(programRequest ? '' : validUntil, supplied, programRequest && bayId ? bayId : null, namedPermits ? permitId : null)} disabled={busy || !valid}
            style={{ ...purpleBtn, opacity: busy || !valid ? 0.6 : 1, cursor: busy || !valid ? 'default' : 'pointer' }}
          >
            {busy ? 'Approving…' : 'Approve parking'}
          </button>
        </div>
      </div>
    </div>
  )
}

/**
 * Add a parker without waiting on the self-registration form — for someone a
 * manager already knows (added by phone, in person). Mints through the exact
 * same seat-cap-checked path as approving a request; the only difference is
 * skipping the pending-request record, so the confirm copy states the grant
 * the same way `ApproveModal` does rather than pretending this is a lighter
 * action.
 */
/**
 * Ask a colleague to register, by email.
 *
 * Grants nothing — it opens the same public sign-up form, and a manager still
 * approves. Deliberately just an address: the email body is composed entirely
 * server-side, which is what keeps this from being a way to send arbitrary mail
 * on Parkify letterhead. A "personal message" box would undo that, so there
 * isn't one.
 */
type EmailEvent = NonNullable<TenantRegistration['emailEvents']>[number]

/**
 * What each sent notice says, in the manager's terms.
 *
 * A Record keyed by the union, NOT a ternary chain. The chain this replaces
 * ended in a catch-all that read "was told the request lapsed and they can
 * register again", so once approval and decline notices started being recorded
 * they rendered as that — wrong for an approval, and actively misleading for a
 * decline, whose whole point is that the plate CANNOT register again. The union
 * was missing both kinds at the time, so nothing flagged it.
 *
 * Keyed exhaustively so the next kind added to `RegistrationEmailKind` fails the
 * build here instead of quietly inheriting somebody else's sentence.
 */
const EMAIL_EVENT_LABEL: Record<RegistrationEmailKind, (name: string, event: EmailEvent) => string> = {
  request_registered: name => `${name} request registered`,
  request_waiting: (name, e) => `${name} has waited ${e.waitingDays ?? 0} days — lapses in ${e.lapsesIn ?? 0}`,
  request_lapsed: name => `${name} was told the request lapsed and they can register again`,
  request_approved: name => `${name} was told their parking is approved`,
  request_declined: name => `${name} was told the request was declined`,
}

export function emailEventLabel(name: string, event: EmailEvent): string {
  // A kind this build doesn't know about (an older portal against a newer API)
  // gets a true, unspecific line rather than the nearest guess. Claiming the
  // wrong thing about a notice we sent is worse than saying little.
  return EMAIL_EVENT_LABEL[event.kind]?.(name, event) ?? `${name} — notice sent`
}

function AddParkerModal({
  busy, permitTypes, namedPermits, platesPerParker, onCancel, onConfirm,
}: {
  busy: boolean
  /** Read-only, from the tenant's location. Absent = unread: fall back to the default, the server still decides. */
  platesPerParker?: number
  permitTypes: string[]
  /** Named permits: pick one of these instead of a type. */
  namedPermits?: NamedPermitOption[] | null
  onCancel: () => void
  onConfirm: (parker: {
    firstName: string; lastName: string; plate?: string; plates: string[]; plateStates: PlateStateInput[]; permitType: string; validUntil: string
    phone: string; email?: string; permitId?: string
  }) => void
}) {
  const [permitId, setPermitId] = useState('')
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [plateStates, setPlateStates] = useState<PlateStateInput[]>([{ plate: '', state: '' }])
  const [phone, setPhone] = useState('')
  const [email, setEmail] = useState('')
  const [permitType, setPermitType] = useState(permitTypes[0] ?? '')
  const [validUntil, setValidUntil] = useState('')
  const [noEndDate, setNoEndDate] = useState(true)
  useEffect(() => {
    if (!permitTypes.includes(permitType)) setPermitType(permitTypes[0] ?? '')
  }, [permitType, permitTypes])
  const today = todayCalendarDate()
  const cleanPlateStates = plateStates.map(vehicle => plateStateInput({
    plate: vehicle.plate.replace(/[^A-Za-z0-9]/g, '').toUpperCase(),
    state: vehicle.state,
  }))
  const cleanPlates = cleanPlateStates.map(vehicle => vehicle.plate)
  const phoneDigits = normalizeParkerPhone(phone)
  const valid = firstName.trim() && lastName.trim() && cleanPlates.length > 0
    && cleanPlates.every(plate => plate.length >= 2 && plate.length <= 12)
    && plateStates.every(vehicle => isPlateStateChoice(vehicle.state))
    && new Set(cleanPlates).size === cleanPlates.length
    && phoneDigits !== null && (noEndDate || (!!validUntil && /^\d{4}-\d{2}-\d{2}$/.test(validUntil) && validUntil >= today))

  const inputStyle: CSSProperties = {
    width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid var(--color-gray-300)',
    fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box',
  }
  const chosen = namedPermits ? !!permitId : !!permitType

  return (
    <div onClick={onCancel} style={overlay}>
      <div onClick={e => e.stopPropagation()} style={{ ...modal, padding: 0 }}>
        <div style={{ padding: '20px 24px 16px', borderBottom: '1px solid var(--color-gray-200)', display: 'flex', justifyContent: 'space-between', gap: 12 }}>
          <div>
            <h3 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Add parker</h3>
            <p style={{ fontSize: 14, fontWeight: 600, color: 'var(--color-gray-600)', margin: '4px 0 0' }}>
              They’ll be able to park as soon as you confirm the billing.
            </p>
          </div>
          <button type="button" aria-label="Close" onClick={onCancel} style={{ background: 'none', border: 0, color: 'var(--color-gray-600)', fontSize: 22, cursor: 'pointer', alignSelf: 'flex-start' }}>×</button>
        </div>
        <div style={{ padding: '20px 24px 8px' }}>
        <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-gray-600)', margin: '0 0 16px', lineHeight: 1.55 }}>
          Add someone you already know. Staff who use the sign-up link land in the approval queue instead.
        </p>

        <div style={{ ...modalSection, display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>First name</div>
            <input value={firstName} onChange={e => setFirstName(e.target.value)} style={inputStyle} autoFocus />
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Last name</div>
            <input value={lastName} onChange={e => setLastName(e.target.value)} style={inputStyle} />
          </div>
        </div>
        <div style={modalSection}>
          {plateStates.map((vehicle, index) => (
            <div className="plate-state-row" key={index} style={{ gap: 8, alignItems: 'end', marginBottom: 10 }}>
              <div>
                <div style={labelStyle}>{index === 0 ? 'License plate' : 'Additional plate'}</div>
                <input
                  aria-label={`Plate ${index + 1}`}
                  value={vehicle.plate}
                  onChange={e => setPlateStates(prev => prev.map((item, i) => i === index ? { ...item, plate: e.target.value.toUpperCase() } : item))}
                  placeholder={index === 0 ? 'ABC 1234' : undefined}
                  style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', letterSpacing: '1px' }}
                />
              </div>
              <div>
                <div style={labelStyle}>State / province</div>
                <select required aria-label={`State / province for plate ${index + 1}`} value={vehicle.state} onChange={e => setPlateStates(prev => prev.map((item, i) => i === index ? { ...item, state: e.target.value } : item))} style={inputStyle}>
                  <option value="">Select a state</option>
                  <option value={PLATE_STATE_UNKNOWN}>{PLATE_STATE_UNKNOWN_LABEL}</option>
                  {PLATE_STATE_GROUPS.map(group => (
                    <optgroup key={group.label} label={group.label}>
                      {group.options.map(option => <option key={option.code} value={option.code}>{option.label}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
              {plateStates.length > 1 && <button className="plate-state-remove" type="button" aria-label={`Remove plate ${index + 1}`} onClick={() => setPlateStates(prev => prev.filter((_, i) => i !== index))} style={ghostBtn}>Remove</button>}
            </div>
          ))}
          {plateStates.length < (platesPerParker ?? DEFAULT_PLATES_PER_PARKER) && (
            <button type="button" onClick={() => setPlateStates(prev => [...prev, { plate: '', state: '' }])} style={{ ...ghostBtn, marginTop: 2 }}>+ Add another plate</button>
          )}
          <PlateLimitNote limit={platesPerParker} count={plateStates.length} />
        </div>

        <div style={{ ...modalSection, display: 'flex', gap: 10 }}>
          <div style={{ flex: 1 }}>
            <ParkerPhoneField value={phone} onChange={setPhone} inputStyle={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={labelStyle}>Email (optional)</div>
            <input type="email" value={email} onChange={e => setEmail(e.target.value)} style={inputStyle} />
          </div>
        </div>

        {namedPermits ? (
          <div style={modalSection}>
            <div style={labelStyle}>Permit</div>
            <select aria-label="Permit" value={permitId} onChange={e => setPermitId(e.target.value)} style={inputStyle}>
              <option value="">Choose a permit</option>
              {namedPermits.map(p => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
        ) : (
        <div style={modalSection}>
          <div style={labelStyle}>Permit type</div>
          <select value={permitType} onChange={e => setPermitType(e.target.value)} style={inputStyle} disabled={permitTypes.length === 0}>
            {permitTypes.map(type => (
              <option key={type} value={type}>{typeLabel(type)}</option>
            ))}
          </select>
          {permitTypes.length === 0 && (
            <p role="alert" style={{ fontSize: 12, fontWeight: 650, color: '#b91c1c', margin: '6px 0 0', lineHeight: 1.5 }}>
              No active permit types are on file. Ask Parkify to add one before adding a parker.
            </p>
          )}
        </div>
        )}

        <div style={labelStyle}>Approved through</div>
        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600, marginBottom: 8 }}>
          <input type="checkbox" checked={noEndDate} onChange={e => { setNoEndDate(e.target.checked); if (e.target.checked) setValidUntil('') }} />
          No end date
        </label>
        {!noEndDate && <DatePicker value={validUntil} min={today} ariaLabel="Permit end date" onChange={date => setValidUntil(date ?? '')} buttonStyle={inputStyle} />}
        <p style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', margin: '6px 0 0', lineHeight: 1.5 }}>
          No end date means you are responsible for removing this parker when they leave.
        </p>

        </div>
        <div style={{ ...modalActions, borderTop: '1px solid var(--color-gray-200)', marginTop: 8, padding: '14px 24px 18px' }}>
          <button onClick={onCancel} style={ghostBtn}>Cancel</button>
          <button
            onClick={() => onConfirm({
              firstName: firstName.trim(), lastName: lastName.trim(), plate: cleanPlates[0], plates: cleanPlates, plateStates: cleanPlateStates, permitType, validUntil: noEndDate ? '' : validUntil,
              ...(namedPermits ? { permitId } : {}),
              phone: phoneDigits ?? '',
              ...(email.trim() ? { email: email.trim() } : {}),
            })}
            disabled={busy || !valid || !chosen}
            style={{ ...purpleBtn, opacity: busy || !valid || !chosen ? 0.6 : 1, cursor: busy || !valid || !chosen ? 'default' : 'pointer' }}
          >
            {busy ? 'Adding…' : 'Add parker'}
          </button>
        </div>
      </div>
    </div>
  )
}

function StatTile({ label, value, color }: { label: string; value: number | string; color?: string }) {
  return (
    <div style={{ background: '#fff', border: '1px solid var(--color-gray-200)', borderRadius: 12, padding: '14px 18px', flex: '1 1 140px', minWidth: 120 }}>
      <div style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.6px', color: color ?? 'var(--color-black)' }}>{value}</div>
      <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-gray-600)', marginTop: 2 }}>{label}</div>
    </div>
  )
}
